// Main menu: title, plane shape/color picker, three launch modes.

import type { PlaneShape, PaperColor } from '../plane/PlaneMesh';

export interface MainMenuChoice {
  shape: PlaneShape;
  color: PaperColor;
  mode: 'play' | 'demo';
}

export type MainMenuCallback = (choice: MainMenuChoice) => void;

const SHAPES: { id: PlaneShape; label: string; subtitle: string }[] = [
  { id: 'classic', label: '经典款', subtitle: '均衡 · 操控顺手' },
  { id: 'dart',    label: '飞镖型', subtitle: '高速 · 转弯锐利' },
  { id: 'glider',  label: '滑翔翼', subtitle: '稳健 · 滞空持久' },
];

const COLORS: { id: PaperColor; label: string; hex: string }[] = [
  { id: 'white',     label: '白纸',   hex: '#fafaf2' },
  { id: 'kraft',     label: '牛皮纸', hex: '#c89a5b' },
  { id: 'newspaper', label: '报纸',   hex: '#cfcfb0' },
  { id: 'red',       label: '红纸',   hex: '#d84a4a' },
  { id: 'blue',      label: '蓝纸',   hex: '#4f7ad8' },
  { id: 'gold',      label: '金箔',   hex: '#e8c450' },
];

export class MainMenu {
  root: HTMLDivElement;
  shape: PlaneShape = 'classic';
  color: PaperColor = 'white';
  private callback: MainMenuCallback;

  constructor(cb: MainMenuCallback) {
    this.callback = cb;
    this.root = document.createElement('div');
    Object.assign(this.root.style, {
      position: 'fixed', inset: '0', zIndex: '110',
      background: 'radial-gradient(ellipse at center, #1a1f3e 0%, #07091c 80%)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      color: '#fff', font: '14px -apple-system, "PingFang SC", sans-serif',
      padding: '24px', overflowY: 'auto',
    } as CSSStyleDeclaration);

    // Title.
    const title = document.createElement('div');
    title.innerHTML = `
      <div style="font-size:44px;font-weight:200;letter-spacing:8px;text-shadow:0 0 18px #88aaff60">PAPER WINGS</div>
      <div style="font-size:14px;letter-spacing:6px;color:#88aaff;margin-top:4px">AI DOGFIGHT · 纸飞机 AI 大战</div>
      <div style="font-size:11px;color:#667;margin-top:14px">DeepSeek-V4-flash 驱动的 6 性格 AI · 语音电台 · 3v3 团战</div>
    `;
    title.style.textAlign = 'center';
    title.style.marginBottom = '22px';
    this.root.appendChild(title);

    // Shape picker.
    this.root.appendChild(this.buildSection('选择机型', SHAPES.map((s) => this.buildShapeCard(s))));

    // Color picker.
    this.root.appendChild(this.buildSection('选择纸色', COLORS.map((c) => this.buildColorChip(c))));

    // Buttons.
    const btnRow = document.createElement('div');
    Object.assign(btnRow.style, { display: 'flex', gap: '10px', marginTop: '20px', flexWrap: 'wrap', justifyContent: 'center' } as CSSStyleDeclaration);
    btnRow.appendChild(this.buildButton('▶ 开始游戏', '#3a8aff', () => this.launch('play')));
    btnRow.appendChild(this.buildButton('🎬 评委讲解 (60秒)', '#ffaa44', () => this.launch('demo')));
    this.root.appendChild(btnRow);

    // Footnote.
    const note = document.createElement('div');
    Object.assign(note.style, { marginTop: '24px', color: '#556', fontSize: '11px', textAlign: 'center', maxWidth: '480px' } as CSSStyleDeclaration);
    note.innerHTML = `
      操作: PC 用 WASD + 鼠标 + Shift 加速 + 鼠标左键开火 · 手机用屏幕摇杆<br/>
      右上"📻"按钮或键盘 T 长按可使用语音电台与 AI 对话
    `;
    this.root.appendChild(note);

    document.body.appendChild(this.root);
  }

  private buildSection(label: string, children: HTMLElement[]): HTMLDivElement {
    const wrap = document.createElement('div');
    Object.assign(wrap.style, { width: '100%', maxWidth: '560px', marginBottom: '14px' } as CSSStyleDeclaration);
    const h = document.createElement('div');
    Object.assign(h.style, { fontSize: '12px', color: '#88aaff', letterSpacing: '2px', marginBottom: '6px' } as CSSStyleDeclaration);
    h.textContent = label;
    wrap.appendChild(h);
    const row = document.createElement('div');
    Object.assign(row.style, { display: 'flex', gap: '8px', flexWrap: 'wrap' } as CSSStyleDeclaration);
    for (const c of children) row.appendChild(c);
    wrap.appendChild(row);
    return wrap;
  }

  private buildShapeCard(s: { id: PlaneShape; label: string; subtitle: string }): HTMLDivElement {
    const card = document.createElement('div');
    Object.assign(card.style, {
      flex: '1 1 0', minWidth: '120px', padding: '10px 12px',
      background: 'rgba(20,30,55,0.7)', border: '2px solid rgba(180,200,255,0.2)',
      borderRadius: '8px', cursor: 'pointer', textAlign: 'center',
    } as CSSStyleDeclaration);
    card.innerHTML = `<div style="font-size:14px;font-weight:600">${s.label}</div><div style="font-size:11px;color:#aab;margin-top:2px">${s.subtitle}</div>`;
    const setSelected = (): void => {
      this.shape = s.id;
      for (const node of card.parentElement!.children) (node as HTMLDivElement).style.borderColor = 'rgba(180,200,255,0.2)';
      card.style.borderColor = '#88ccff';
    };
    card.addEventListener('click', setSelected);
    if (s.id === this.shape) setTimeout(setSelected, 0);
    return card;
  }

  private buildColorChip(c: { id: PaperColor; label: string; hex: string }): HTMLDivElement {
    const chip = document.createElement('div');
    Object.assign(chip.style, {
      padding: '8px', background: 'rgba(20,30,55,0.7)',
      border: '2px solid rgba(180,200,255,0.2)', borderRadius: '6px',
      cursor: 'pointer', textAlign: 'center', minWidth: '70px',
    } as CSSStyleDeclaration);
    chip.innerHTML = `
      <div style="width:32px;height:24px;background:${c.hex};border-radius:3px;margin:0 auto 4px"></div>
      <div style="font-size:11px">${c.label}</div>
    `;
    const setSelected = (): void => {
      this.color = c.id;
      for (const node of chip.parentElement!.children) (node as HTMLDivElement).style.borderColor = 'rgba(180,200,255,0.2)';
      chip.style.borderColor = '#88ccff';
    };
    chip.addEventListener('click', setSelected);
    if (c.id === this.color) setTimeout(setSelected, 0);
    return chip;
  }

  private buildButton(label: string, color: string, on: () => void): HTMLButtonElement {
    const b = document.createElement('button');
    b.textContent = label;
    Object.assign(b.style, {
      padding: '10px 22px', border: '0', background: color,
      color: '#fff', borderRadius: '24px', fontSize: '14px',
      letterSpacing: '2px', cursor: 'pointer', fontWeight: '600',
      boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
    } as CSSStyleDeclaration);
    b.addEventListener('click', on);
    return b;
  }

  private launch(mode: 'play' | 'demo'): void {
    this.callback({ shape: this.shape, color: this.color, mode });
  }

  destroy(): void {
    this.root.remove();
  }
}
